// InternalConnection.jsx - Connessioni interne per compound nodes
// Gestisce le connessioni tra MatchNode all'interno di un CompoundNode
import React, { useState, useMemo } from 'react';
import MatchNode from './MatchNode';

const InternalConnection = ({
  connection,
  matches,
  parentOffset = { x: 0, y: 0 },
  onDelete,
  selected = false,
  onSelect
}) => {
  const [hovered, setHovered] = useState(false);

  // Trova i match sorgente e destinazione
  const fromMatch = matches.find(m => m.id === connection.fromMatchId);
  const toMatch = matches.find(m => m.id === connection.toMatchId);

  // Calcola le posizioni delle porte
  const positions = useMemo(() => {
    if (!fromMatch || !toMatch) return null;

    const fromPort = MatchNode.PORTS.outputs.find(p => p.id === connection.fromPort);
    const toPort = MatchNode.PORTS.inputs.find(p => p.id === connection.toPort);

    if (!fromPort || !toPort) return null;

    // Posizione output (destra del match sorgente)
    const fromX = fromMatch.x + parentOffset.x + MatchNode.WIDTH + 6;
    const fromY = fromMatch.y + parentOffset.y + MatchNode.HEIGHT * fromPort.yRatio;

    // Posizione input (sinistra del match destinazione)
    const toX = toMatch.x + parentOffset.x - 6;
    const toY = toMatch.y + parentOffset.y + MatchNode.HEIGHT * toPort.yRatio;

    return { fromX, fromY, toX, toY };
  }, [fromMatch, toMatch, connection, parentOffset]);

  if (!positions) return null;

  const { fromX, fromY, toX, toY } = positions;

  // Crea il percorso curvo
  const midX = (fromX + toX) / 2;
  const controlX1 = fromX + Math.abs(toX - fromX) * 0.4;
  const controlX2 = toX - Math.abs(toX - fromX) * 0.4;

  const pathData = `M ${fromX} ${fromY} C ${controlX1} ${fromY} ${controlX2} ${toY} ${toX} ${toY}`;

  // Colore basato sul tipo di porta sorgente
  const getConnectionColor = () => {
    if (selected) return '#1976D2';
    if (connection.fromPort === 'winner') {
      return hovered ? '#388E3C' : '#4CAF50';
    } else {
      return hovered ? '#D32F2F' : '#f44336';
    }
  };

  const getStrokeWidth = () => {
    if (selected) return 3;
    return hovered ? 2.5 : 2;
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(connection.id);
  };

  const handleClick = (e) => {
    e.stopPropagation();
    onSelect?.(connection);
  };

  // Punto medio per pulsante elimina
  const middleX = midX;
  const middleY = (fromY + toY) / 2;

  // ID univoco per il marker
  const markerId = `internal-arrow-${connection.id}`;

  return (
    <g
      className={`internal-connection ${hovered ? 'hovered' : ''} ${selected ? 'selected' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={handleClick}
    >
      {/* Definizione marker freccia */}
      <defs>
        <marker
          id={markerId}
          markerWidth="8"
          markerHeight="6"
          refX="7"
          refY="3"
          orient="auto"
        >
          <polygon
            points="0 0, 8 3, 0 6"
            fill={getConnectionColor()}
          />
        </marker>
      </defs>

      {/* Percorso principale */}
      <path
        d={pathData}
        fill="none"
        stroke={getConnectionColor()}
        strokeWidth={getStrokeWidth()}
        markerEnd={`url(#${markerId})`}
        className="connection-path"
        style={{ cursor: 'pointer' }}
      />

      {/* Percorso invisibile per hover più facile */}
      <path
        d={pathData}
        fill="none"
        stroke="transparent"
        strokeWidth="12"
        style={{ cursor: 'pointer' }}
      />

      {/* Etichetta tipo connessione */}
      {(hovered || selected) && (
        <g>
          <rect
            x={middleX - 12}
            y={middleY - 8}
            width={24}
            height={16}
            fill="white"
            stroke={getConnectionColor()}
            strokeWidth="1"
            rx="3"
          />
          <text
            x={middleX}
            y={middleY + 4}
            textAnchor="middle"
            fill={getConnectionColor()}
            fontSize="10"
            fontWeight="bold"
            pointerEvents="none"
          >
            {connection.fromPort === 'winner' ? 'V' : 'P'}
          </text>
        </g>
      )}

      {/* Pulsante elimina */}
      {hovered && (
        <g className="delete-button" onClick={handleDelete} style={{ cursor: 'pointer' }}>
          <circle
            cx={middleX + 20}
            cy={middleY}
            r="8"
            fill="#f44336"
            stroke="#fff"
            strokeWidth="1.5"
          />
          <text
            x={middleX + 20}
            y={middleY + 3}
            textAnchor="middle"
            fill="white"
            fontSize="10"
            fontWeight="bold"
            pointerEvents="none"
          >
            ×
          </text>
        </g>
      )}
    </g>
  );
};

export default InternalConnection;
